import React, { useRef } from 'react';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number;
}

export interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
  /**
   * - tabs (défaut) : de vrais onglets qui changent le contenu affiché dessous. Motif ARIA complet :
   *   tablist / tab / tabpanel liés par id, un seul onglet dans l'ordre de tabulation, flèches,
   *   Début et Fin pour passer de l'un à l'autre. Le panneau reçoit `tabPanelProps(idPrefix, id)`.
   * - filter : de simples filtres d'une liste (boutons à bascule `aria-pressed` dans un groupe).
   */
  variant?: 'tabs' | 'filter';
  /** Préfixe des id d'onglets et de panneaux (variante tabs) ; unique dans la page. */
  idPrefix?: string;
  /** Nom accessible du groupe d'onglets ou de filtres. */
  ariaLabel?: string;
}

/** Attributs du panneau d'un onglet (variante tabs). */
export function tabPanelProps(idPrefix: string, tabId: string) {
  return {
    role: 'tabpanel' as const,
    id: `${idPrefix}-panel-${tabId}`,
    'aria-labelledby': `${idPrefix}-tab-${tabId}`,
    tabIndex: 0,
  };
}

/**
 * Onglets "segmentés" : une piste crème, l'onglet actif en blanc chaud.
 * Même vocabulaire que les filtres du site, lisible d'un coup d'œil.
 */
const Tabs: React.FC<TabsProps> = ({
  tabs, activeTab, onChange, className = '', variant = 'tabs', idPrefix = 'tabs', ariaLabel,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const isTabs = variant === 'tabs';

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isTabs) return;
    const index = tabs.findIndex(t => t.id === activeTab);
    let next = -1;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    onChange(tab.id);
    listRef.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${idPrefix}-tab-${tab.id}`)}`)?.focus();
  };

  return (
    <div
      ref={listRef}
      className={`inline-flex max-w-full overflow-x-auto scroll-thin p-1 rounded-xl bg-cream-200 dark:bg-gray-800 ${className}`}
      role={isTabs ? 'tablist' : 'group'}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const a11y = isTabs
          ? {
              role: 'tab' as const,
              id: `${idPrefix}-tab-${tab.id}`,
              'aria-selected': isActive,
              'aria-controls': `${idPrefix}-panel-${tab.id}`,
              tabIndex: isActive ? 0 : -1,
            }
          : { 'aria-pressed': isActive };
        return (
          <button
            key={tab.id}
            type="button"
            {...a11y}
            onClick={() => onChange(tab.id)}
            className={`
              inline-flex items-center gap-2 h-8 px-3.5 rounded-lg text-sm font-medium leading-none whitespace-nowrap
              transition-all duration-200
              ${isActive
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }
            `}
          >
            {tab.icon && <span aria-hidden="true" className="inline-flex [&>svg]:w-4 [&>svg]:h-4">{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className={`
                min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-xs font-bold rounded-full
                ${isActive ? 'bg-brand-500 text-cream' : 'bg-white/70 dark:bg-gray-900 text-gray-700 dark:text-gray-300'}
              `}>
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
